import { cn } from '@/lib/utils';
import { useEffect, useRef, useState } from 'react';

export type EditableFieldValue = string | number;

interface EditableFieldProps {
  value: EditableFieldValue;
  className?: string;
  onChange: (newValue: EditableFieldValue) => void;
  element?: 'input' | 'textarea';
  editAction?: 'click' | 'double-click';
  onClick?: (event: React.MouseEvent) => void;
  selectAllOnEdit?: boolean;
}

export const EditableInput = ({
  value,
  onChange,
  element = 'input',
  className,
  editAction = 'click',
  onClick,
  selectAllOnEdit = false,
}: EditableFieldProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState<EditableFieldValue>(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (value !== localValue && !isEditing) {
      setLocalValue(value);
    }
  }, [value]);

  const handleStartEditing = (event: React.MouseEvent) => {
    event.stopPropagation();
    setIsEditing(true);
    setTimeout(() => {
      inputRef.current?.focus();
      textareaRef.current?.focus();
      if (selectAllOnEdit) {
        inputRef.current?.select();
        textareaRef.current?.select();
      }
    }, 1);
  };

  const handleSave = (event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation();
    }

    setIsEditing(false);

    if (localValue !== value) {
      onChange(localValue);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setLocalValue(e.target.value);
  };

  const enterSave = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    }
  };

  const handleBlur = () => {
    handleSave();
  };

  const stopClickPropagation = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  const renderEditField = () => {
    if (element === 'textarea') {
      return (
        <textarea
          ref={textareaRef}
          className="border border-gray-300 p-1 rounded w-full"
          value={localValue as string}
          onClick={(e) => stopClickPropagation(e)}
          onChange={handleChange}
          onBlur={handleBlur}
          rows={4}
        />
      );
    }

    return (
      <input
        type="text"
        ref={inputRef}
        className="border border-gray-300 px-2 -ml-2 py-1.5 rounded z-10 h-9 w-full"
        value={localValue as string}
        onClick={(e) => stopClickPropagation(e)}
        onChange={handleChange}
        onKeyDown={enterSave}
        onBlur={handleBlur}
      />
    );
  };

  const renderValue = () => {
    if (typeof localValue === 'string' && !localValue) {
      return <p className="text-gray-400 text-sm">Click to edit</p>;
    }

    return (
      <p
        className={cn('px-0 py-1.5', {
          'truncate h-9': element === 'input',
          'whitespace-pre-wrap w-fit h-fit max-h-7 ': element === 'textarea',
        })}
      >
        {localValue}
      </p>
    );
  };

  return (
    <div className={cn('', className)}>
      {isEditing ? (
        renderEditField()
      ) : (
        <div
          onClick={editAction === 'click' ? handleStartEditing : onClick}
          onDoubleClick={editAction === 'double-click' ? handleStartEditing : undefined}
          className="flex items-center justify-between w-full cursor-pointer"
        >
          {renderValue()}
        </div>
      )}
    </div>
  );
};
